import React, { useState } from "react";
import { Link } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select from "react-select";
import ajaxAccounting, { ajaxMigration } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import { canAddRow, addRowBlockedReason } from "../../util/accounting/formRows";
import toast, { Toaster } from "react-hot-toast";

const STEPS = [
  { id: 1, title: "Setup",           icon: "fa-gear" },
  { id: 2, title: "Opening Balances",icon: "fa-scale-balanced" },
  { id: 3, title: "Outstanding AR",  icon: "fa-file-invoice-dollar" },
  { id: 4, title: "Outstanding AP",  icon: "fa-cart-shopping" },
  { id: 5, title: "Complete",        icon: "fa-circle-check" },
];

const StepBar = ({ current }) => (
  <div className="d-flex align-items-center justify-content-center mb-4">
    {STEPS.map((s, i) => (
      <React.Fragment key={s.id}>
        <div className="d-flex flex-column align-items-center" style={{ minWidth: "80px" }}>
          <div style={{
            width: "36px", height: "36px", borderRadius: "50%",
            background: s.id < current ? "#10b981" : s.id === current ? "#4f46e5" : "#e5e7eb",
            color: s.id <= current ? "#fff" : "#9ca3af",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: "0.8rem",
            boxShadow: s.id === current ? "0 0 0 4px rgba(79,70,229,0.15)" : "none",
          }}>
            {s.id < current ? <i className="fa-solid fa-check"></i> : <i className={`fa-solid ${s.icon}`} style={{ fontSize: "0.8rem" }}></i>}
          </div>
          <div style={{ fontSize: "0.65rem", marginTop: "4px", color: s.id === current ? "#4f46e5" : "#9ca3af", fontWeight: s.id === current ? 600 : 400, textAlign: "center" }}>
            {s.title}
          </div>
        </div>
        {i < STEPS.length - 1 && (
          <div style={{ flex: 1, height: "2px", background: s.id < current ? "#10b981" : "#e5e7eb", margin: "0 2px", marginBottom: "18px" }} />
        )}
      </React.Fragment>
    ))}
  </div>
);

export default function MigrationAssistant() {
  const [step,       setStep]       = useState(1);
  const [fyList,     setFyList]     = useState([]);
  const [accounts,   setAccounts]   = useState([]);
  const [targetFY,   setTargetFY]   = useState(null);
  const [balRows,    setBalRows]    = useState([{ account_id: null, debit: "", credit: "", description: "" }]);
  // A row earns another only once it says something.
  const BAL_REQUIRED = [
    { key: "account_id", label: "an account" },
    { key: "amount", label: "a debit or a credit", numeric: true },
  ];
  const INVOICE_REQUIRED = [
    { key: "contact_name", label: "a name" },
    { key: "amount", label: "an amount", numeric: true },
  ];
  // Opening balances carry a debit or a credit, never both.
  const withAmount = (rows) =>
    rows.map((r) => ({ ...r, amount: (parseFloat(r.debit) || 0) || (parseFloat(r.credit) || 0) }));
  const [arRows,     setArRows]     = useState([{ contact_name: "", invoice_number: "", amount: "", due_date: "", description: "" }]);
  const [apRows,     setApRows]    = useState([{ contact_name: "", invoice_number: "", amount: "", due_date: "", description: "" }]);
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [csvMode,    setCsvMode]    = useState(false);
  const [csvText,    setCsvText]    = useState("");

  React.useEffect(() => { fetchMeta(); }, []);

  const fetchMeta = async () => {
    setLoading(true);
    const [fyRes, accRes] = await Promise.all([
      ajaxAccounting.listFinancialYears({}),
      ajaxAccounting.listAccounts({ postable_only: 1 }),
    ]);
    if (fyRes.status === "OK") setFyList(fyRes.details || []);
    if (accRes.status === "OK") setAccounts(accRes.details || []);
    setLoading(false);
  };

  const accountOptions = accounts.map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }));
  const fyOptions = fyList.map((y) => ({ value: y.id, label: y.name }));

  // ── OPENING BALANCES ──────────────────────────────────────────────────────
  const addBalRow    = () => setBalRows([...balRows, { account_id: null, debit: "", credit: "", description: "" }]);
  const removeBalRow = (i) => balRows.length > 1 && setBalRows(balRows.filter((_, j) => j !== i));
  const setBalRow    = (i, f, v) => { const u = [...balRows]; u[i] = { ...u[i], [f]: v }; setBalRows(u); };

  const parseBalCSV = () => {
    const lines = csvText.trim().split("\n").filter((l) => l.trim()).slice(1);
    const parsed = lines.map((line) => {
      const [code, , debit, credit, desc] = line.split(",").map((v) => v.trim().replace(/"/g, ""));
      const acc = accounts.find((a) => a.account_code === code);
      return { account_id: acc ? { value: acc.id, label: `${acc.account_code} — ${acc.name}` } : null, debit: debit || "", credit: credit || "", description: desc || "" };
    });
    setBalRows(parsed);
    setCsvMode(false);
    toast.success(`${parsed.length} rows imported from CSV`);
  };

  const submitBalances = async () => {
    if (!targetFY) { toast.error("Select target financial year"); return; }
    const valid = balRows.filter((r) => r.account_id && (parseFloat(r.debit) || parseFloat(r.credit)));
    if (!valid.length) { toast.error("Add at least one balance entry"); return; }
    const totalDr = valid.reduce((s, r) => s + (parseFloat(r.debit) || 0), 0);
    const totalCr = valid.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0);
    if (Math.abs(totalDr - totalCr) > 0.01) {
      toast.error(`Debits (${functions.formatCurrency(totalDr)}) ≠ Credits (${functions.formatCurrency(totalCr)}). Difference: ${functions.formatCurrency(Math.abs(totalDr - totalCr))}`);
      return;
    }
    setSaving(true);
    const res = await ajaxMigration.importOpeningBalances({
      financial_year_id: targetFY.value,
      balances: valid.map((r) => ({
        account_id:  r.account_id.value,
        debit:       parseFloat(r.debit)  || 0,
        credit:      parseFloat(r.credit) || 0,
        description: r.description,
      })),
    });
    if (res.status === "OK") { toast.success("Opening balances imported!"); setStep(3); }
    else toast.error(res.message);
    setSaving(false);
  };

  // ── AR / AP ────────────────────────────────────────────────────────────────
  const addArRow    = (arr, setArr) => setArr([...arr, { contact_name: "", invoice_number: "", amount: "", due_date: "", description: "" }]);
  const removeArRow = (arr, setArr, i) => arr.length > 1 && setArr(arr.filter((_, j) => j !== i));
  const setArRow    = (arr, setArr, i, f, v) => { const u = [...arr]; u[i] = { ...u[i], [f]: v }; setArr(u); };

  const submitAR = async () => {
    setSaving(true);
    const valid = arRows.filter((r) => r.contact_name && r.amount);
    if (!valid.length) { toast.error("Add at least one outstanding invoice"); setSaving(false); return; }
    const res = await ajaxMigration.migrateInvoices({ financial_year_id: targetFY?.value, type: "outgoing", invoices: valid });
    if (res.status === "OK") { toast.success("Outstanding receivables imported!"); setStep(4); }
    else toast.error(res.message);
    setSaving(false);
  };

  const submitAP = async () => {
    setSaving(true);
    const valid = apRows.filter((r) => r.contact_name && r.amount);
    const res = await ajaxMigration.migrateInvoices({ financial_year_id: targetFY?.value, type: "incoming", invoices: valid });
    if (res.status === "OK") { toast.success("Outstanding payables imported!"); setStep(5); }
    else toast.error(res.message);
    setSaving(false);
  };

  const totalDr = balRows.reduce((s, r) => s + (parseFloat(r.debit) || 0), 0);
  const totalCr = balRows.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.01 && balRows.some((r) => r.account_id);

  return (
    <AppContainer
      title="Migration Assistant"
      previousPageTitle="Accounting"
      description="Moving from another accounting system? Import your opening balances, outstanding customer invoices, and supplier invoices to start fresh in Accountify."
    >
      <Toaster />
      <StepBar current={step} />

      {/* STEP 1 — SETUP */}
      {step === 1 && (
        <div className="card custom-card p-4">
          <h5 className="fw-semibold mb-2">Migration Setup</h5>
          <p className="tx-muted tx-13 mb-4">
            This wizard imports your historical data from a previous accounting system.
            Select the financial year you're migrating data into, then follow each step.
          </p>
          <div className="row g-3 mb-4">
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Target Financial Year <span className="tx-danger">*</span></label>
              <Select options={fyOptions} value={targetFY} onChange={setTargetFY} placeholder="Select financial year" isSearchable isLoading={loading} />
              <small className="tx-muted">This is the year balances will be imported into.</small>
            </div>
          </div>
          <div className="alert alert-warning tx-13 mb-4">
            <strong><i className="fa-solid fa-triangle-exclamation me-1"></i>Before you start:</strong>
            <ul className="mb-0 mt-1">
              <li>Ensure your Chart of Accounts is set up with the same account codes as your old system</li>
              <li>Get your trial balance from your old system as at the migration date</li>
              <li>Collect a list of all unpaid customer and supplier invoices</li>
              <li>Opening balances must balance (Total Debits = Total Credits)</li>
            </ul>
          </div>
          <div className="row g-3 mb-4">
            {[
              { icon: "fa-scale-balanced", color: "#6366f1", label: "Opening Balances",       desc: "Import trial balance from old system" },
              { icon: "fa-file-invoice-dollar", color: "#10b981", label: "Outstanding AR",     desc: "Unpaid customer invoices to carry over" },
              { icon: "fa-cart-shopping", color: "#f59e0b", label: "Outstanding AP",           desc: "Unpaid supplier invoices to carry over" },
            ].map((card) => (
              <div key={card.label} className="col-lg-4">
                <div className="p-3 rounded d-flex align-items-start gap-3" style={{ border: "1px solid #e5e7eb" }}>
                  <div style={{ width: "38px", height: "38px", borderRadius: "8px", background: card.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className={`fa-solid ${card.icon}`} style={{ color: card.color }}></i>
                  </div>
                  <div>
                    <div className="fw-semibold tx-13">{card.label}</div>
                    <div className="tx-12 tx-muted">{card.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-end">
            <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!targetFY}>
              Start Migration <i className="fa-solid fa-arrow-right ms-1"></i>
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — OPENING BALANCES */}
      {step === 2 && (
        <div className="card custom-card p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="fw-semibold mb-0">Opening Balances</h5>
              <p className="tx-muted tx-12 mb-0">Enter your trial balance as at migration date. Must balance.</p>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setCsvMode(!csvMode)}>
                <i className="fa-solid fa-file-csv me-1"></i>{csvMode ? "Manual Entry" : "Import CSV"}
              </button>
              <button className="btn btn-sm btn-outline-primary" onClick={addBalRow}
                disabled={!canAddRow(withAmount(balRows), BAL_REQUIRED)}
                title={addRowBlockedReason(withAmount(balRows), BAL_REQUIRED, "row") || "Add another row"}>
                <i className="fa-solid fa-plus me-1"></i>Add Row
              </button>
            </div>
          </div>

          {csvMode ? (
            <div className="mb-3">
              <div className="alert alert-info tx-12">
                CSV format: <code>account_code, account_name, debit, credit, description</code>
              </div>
              <textarea className="form-control font-monospace" rows={8} value={csvText} onChange={(e) => setCsvText(e.target.value)}
                placeholder={"account_code,account_name,debit,credit,description\n1010,Cash in Hand,5000000,0,Opening balance\n2010,Accounts Payable,0,3000000,Opening balance"} style={{ fontSize: "0.8rem" }} />
              <button className="btn btn-primary mt-2" onClick={parseBalCSV}>Parse CSV</button>
            </div>
          ) : (
            <div className="table-responsive mb-3">
              <table className="table table-bordered" style={{ fontSize: "0.84rem" }}>
                <thead className="bg-light">
                  <tr><th style={{ minWidth: "240px" }}>Account</th><th style={{ width: "160px" }}>Debit (Dr)</th><th style={{ width: "160px" }}>Credit (Cr)</th><th>Description</th><th style={{ width: "36px" }}></th></tr>
                </thead>
                <tbody>
                  {balRows.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <Select options={accountOptions} value={row.account_id} onChange={(v) => setBalRow(i, "account_id", v)} placeholder="Select account" isSearchable />
                      </td>
                      <td><input type="number" className="form-control form-control-sm" min="0" step="0.01" value={row.debit} onChange={(e) => setBalRow(i, "debit", e.target.value)} placeholder="0.00" /></td>
                      <td><input type="number" className="form-control form-control-sm" min="0" step="0.01" value={row.credit} onChange={(e) => setBalRow(i, "credit", e.target.value)} placeholder="0.00" /></td>
                      <td><input className="form-control form-control-sm" value={row.description} onChange={(e) => setBalRow(i, "description", e.target.value)} placeholder="Opening balance" /></td>
                      <td>{balRows.length > 1 && <button type="button" className="btn btn-sm btn-light tx-danger" onClick={() => removeBalRow(i)}><i className="fa-solid fa-trash"></i></button>}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-light fw-semibold">
                  <tr>
                    <td className="text-end">Totals</td>
                    <td className="tx-success">{functions.formatCurrency(totalDr)}</td>
                    <td className="tx-danger">{functions.formatCurrency(totalCr)}</td>
                    <td colSpan={2}>
                      {balanced
                        ? <span className="badge bg-success"><i className="fa-solid fa-check me-1"></i>Balanced</span>
                        : balRows.some((r) => r.account_id) && <span className="badge bg-danger balance-out">Out of balance: {functions.formatCurrency(Math.abs(totalDr - totalCr))}</span>}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="d-flex justify-content-between">
            <button className="btn btn-outline-secondary" onClick={() => setStep(1)}><i className="fa-solid fa-arrow-left me-1"></i>Back</button>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={() => setStep(3)}>Skip <i className="fa-solid fa-forward ms-1"></i></button>
              <button className="btn btn-primary" onClick={submitBalances} disabled={saving || !balanced}>
                {saving ? "Importing..." : <>Import Balances <i className="fa-solid fa-arrow-right ms-1"></i></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3 — OUTSTANDING AR */}
      {step === 3 && (
        <div className="card custom-card p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="fw-semibold mb-0">Outstanding Customer Invoices (AR)</h5>
              <p className="tx-muted tx-12 mb-0">Enter all unpaid customer invoices as at migration date.</p>
            </div>
            <button className="btn btn-sm btn-outline-primary" onClick={() => addArRow(arRows, setArRows)}
              disabled={!canAddRow(arRows, INVOICE_REQUIRED)}
              title={addRowBlockedReason(arRows, INVOICE_REQUIRED, "row") || "Add another row"}>
              <i className="fa-solid fa-plus me-1"></i>Add Row
            </button>
          </div>
          <div className="table-responsive mb-3">
            <table className="table table-bordered" style={{ fontSize: "0.84rem" }}>
              <thead className="bg-light">
                <tr><th>Customer Name</th><th>Invoice #</th><th>Amount Due</th><th>Due Date</th><th>Description</th><th style={{ width: "36px" }}></th></tr>
              </thead>
              <tbody>
                {arRows.map((row, i) => (
                  <tr key={i}>
                    <td><input className="form-control form-control-sm" value={row.contact_name} onChange={(e) => setArRow(arRows, setArRows, i, "contact_name", e.target.value)} placeholder="Customer name" /></td>
                    <td><input className="form-control form-control-sm" value={row.invoice_number} onChange={(e) => setArRow(arRows, setArRows, i, "invoice_number", e.target.value)} placeholder="INV-001" /></td>
                    <td><input type="number" className="form-control form-control-sm" min="0" step="0.01" value={row.amount} onChange={(e) => setArRow(arRows, setArRows, i, "amount", e.target.value)} placeholder="0.00" /></td>
                    <td><input type="date" className="form-control form-control-sm" value={row.due_date} onChange={(e) => setArRow(arRows, setArRows, i, "due_date", e.target.value)} /></td>
                    <td><input className="form-control form-control-sm" value={row.description} onChange={(e) => setArRow(arRows, setArRows, i, "description", e.target.value)} /></td>
                    <td>{arRows.length > 1 && <button type="button" className="btn btn-sm btn-light tx-danger" onClick={() => removeArRow(arRows, setArRows, i)}><i className="fa-solid fa-trash"></i></button>}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-light fw-semibold">
                <tr>
                  <td colSpan={2}>Total Outstanding AR</td>
                  <td className="tx-primary">{functions.formatCurrency(arRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="d-flex justify-content-between">
            <button className="btn btn-outline-secondary" onClick={() => setStep(2)}><i className="fa-solid fa-arrow-left me-1"></i>Back</button>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={() => setStep(4)}>Skip <i className="fa-solid fa-forward ms-1"></i></button>
              <button className="btn btn-primary" onClick={submitAR} disabled={saving}>
                {saving ? "Importing..." : <>Import AR <i className="fa-solid fa-arrow-right ms-1"></i></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4 — OUTSTANDING AP */}
      {step === 4 && (
        <div className="card custom-card p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="fw-semibold mb-0">Outstanding Supplier Invoices (AP)</h5>
              <p className="tx-muted tx-12 mb-0">Enter all unpaid supplier invoices as at migration date.</p>
            </div>
            <button className="btn btn-sm btn-outline-primary" onClick={() => addArRow(apRows, setApRows)}
              disabled={!canAddRow(apRows, INVOICE_REQUIRED)}
              title={addRowBlockedReason(apRows, INVOICE_REQUIRED, "row") || "Add another row"}>
              <i className="fa-solid fa-plus me-1"></i>Add Row
            </button>
          </div>
          <div className="table-responsive mb-3">
            <table className="table table-bordered" style={{ fontSize: "0.84rem" }}>
              <thead className="bg-light">
                <tr><th>Supplier Name</th><th>Invoice #</th><th>Amount Due</th><th>Due Date</th><th>Description</th><th style={{ width: "36px" }}></th></tr>
              </thead>
              <tbody>
                {apRows.map((row, i) => (
                  <tr key={i}>
                    <td><input className="form-control form-control-sm" value={row.contact_name} onChange={(e) => setArRow(apRows, setApRows, i, "contact_name", e.target.value)} placeholder="Supplier name" /></td>
                    <td><input className="form-control form-control-sm" value={row.invoice_number} onChange={(e) => setArRow(apRows, setApRows, i, "invoice_number", e.target.value)} placeholder="SUPP-INV-001" /></td>
                    <td><input type="number" className="form-control form-control-sm" min="0" step="0.01" value={row.amount} onChange={(e) => setArRow(apRows, setApRows, i, "amount", e.target.value)} placeholder="0.00" /></td>
                    <td><input type="date" className="form-control form-control-sm" value={row.due_date} onChange={(e) => setArRow(apRows, setApRows, i, "due_date", e.target.value)} /></td>
                    <td><input className="form-control form-control-sm" value={row.description} onChange={(e) => setArRow(apRows, setApRows, i, "description", e.target.value)} /></td>
                    <td>{apRows.length > 1 && <button type="button" className="btn btn-sm btn-light tx-danger" onClick={() => removeArRow(apRows, setApRows, i)}><i className="fa-solid fa-trash"></i></button>}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-light fw-semibold">
                <tr>
                  <td colSpan={2}>Total Outstanding AP</td>
                  <td className="tx-danger">{functions.formatCurrency(apRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="d-flex justify-content-between">
            <button className="btn btn-outline-secondary" onClick={() => setStep(3)}><i className="fa-solid fa-arrow-left me-1"></i>Back</button>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={() => setStep(5)}>Skip <i className="fa-solid fa-forward ms-1"></i></button>
              <button className="btn btn-primary" onClick={submitAP} disabled={saving}>
                {saving ? "Importing..." : <>Import AP <i className="fa-solid fa-arrow-right ms-1"></i></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 5 — COMPLETE */}
      {step === 5 && (
        <div className="card custom-card p-4 text-center py-5">
          <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "#d1fae5", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="fa-solid fa-circle-check" style={{ fontSize: "2.5rem", color: "#10b981" }}></i>
          </div>
          <h4 className="fw-bold mb-2">Migration Complete!</h4>
          <p className="tx-muted mb-4">
            Your opening balances, outstanding receivables, and payables have been imported.<br/>
            You are now ready to start recording new transactions in Accountify.
          </p>
          <div className="row g-2 justify-content-center mb-4">
            {[
              { label: "View Ledger",          link: "/accounting/ledger",                icon: "fa-table-list" },
              { label: "Chart of Accounts",    link: "/accounting/accounts",              icon: "fa-sitemap" },
              { label: "Outstanding Invoices", link: "/accounting/invoices/aging?type=outgoing", icon: "fa-file-invoice-dollar" },
              { label: "Go to Dashboard",      link: "/",                                 icon: "fa-chart-line" },
            ].map((q) => (
              <div key={q.link} className="col-lg-3 col-sm-6">
                <Link to={q.link} className="btn btn-outline-primary w-100">
                  <i className={`fa-solid ${q.icon} me-1`}></i>{q.label}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppContainer>
  );
}
